(function() {
  function initHeader() {
    const header = document.querySelector('.header__container');
    const filterBar = document.querySelector('.collection-filter-bar');
    const announcementBar = document.querySelector('#shopify-section-announcement-bar .announcement-bar__container');
    if (!header) return;

    // The header's fixed positioning and scroll hide/show are driven entirely by GSAP,
    // loaded from a third-party CDN (see theme.liquid). If that request is blocked or
    // slow, the first gsap.* call below would throw and abort init mid-run, leaving
    // .header__container in normal document flow. Bail gracefully instead: the CSS
    // fallback keeps the fixed mobile menu pinned to the viewport; the header simply
    // won't animate this session rather than breaking the menu.
    if (typeof gsap === 'undefined' || !window.gsap) return;

    const passiveOptions = { passive: true };
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isNotCollectionPage = !document.body.classList.contains('template-collection');

    let lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    let lastKnownScrollY = lastScrollY;
    let isTicking = false;
    // Width at last resize. iOS Safari fires `resize` while scrolling (the URL toolbar
    // collapses/expands, changing innerHeight); a genuine resize also changes the width.
    // We use this to tell the two apart in handleResize.
    let lastWindowWidth = window.innerWidth;

    let headerHeight = header.offsetHeight || 0;
    let announcementHeight = announcementBar ? announcementBar.offsetHeight || 0 : 0;
    let filterBarHeight = filterBar ? filterBar.offsetHeight || 0 : 0;

    const SCROLL_BUFFER = 12; // ignore micro-jitter
    const TOP_EPSILON = 1;
    const DURATION = 0.25;
    const EASE = 'power2.out';
    // A direction must persist this many consecutive frames before it toggles the
    // header. Safari momentum / rubber-band scrolling can report a single transient
    // backward frame mid-flick; without this guard that one frame flips the header
    // visible-then-hidden (the "logo jumps / reappears" flicker on fast scroll).
    const CONFIRM_FRAMES = 2;

    let isHeaderVisible = true;
    let isAnnouncementVisible = true;

    // Tracks the scroll direction that is currently being confirmed and how many
    // consecutive frames it has held (see CONFIRM_FRAMES). 1 = down, -1 = up, 0 = none.
    let pendingDir = 0;
    let dirStreak = 0;

    let headerTween = null;
    let filterTween = null;
    let announcementTween = null;

    function getScrollY() {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      return y < 0 ? 0 : y;
    }

    function updateHeights() {
      headerHeight = header.offsetHeight || 0;
      announcementHeight = announcementBar ? announcementBar.offsetHeight || 0 : 0;
      filterBarHeight = filterBar ? filterBar.offsetHeight || 0 : 0;

      document.documentElement.style.setProperty("--announcement-bar-height", announcementHeight + "px");

      const scrollY = getScrollY();
      isAnnouncementVisible = announcementBar ? scrollY <= TOP_EPSILON : false;
      isHeaderVisible = true;

      if (announcementBar) {
        gsap.set(announcementBar, {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 1002,
          willChange: 'transform',
          background: 'black',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          y: isAnnouncementVisible ? 0 : -announcementHeight
        });
      }

      const headerY = isAnnouncementVisible && announcementBar ? announcementHeight : 0;

      gsap.set(header, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 1001,
        willChange: 'transform',
        background: 'black',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        y: headerY
      });

      if (filterBar) {
        const filterY = isAnnouncementVisible && announcementBar
          ? announcementHeight + headerHeight
          : headerHeight;

        gsap.set(filterBar, {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 4,
          willChange: 'transform',
          background: 'black',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          y: filterY
        });

        filterBar.classList.add('is-fixed');

        const contentBelowFilter = filterBar.nextElementSibling;
        if (contentBelowFilter) {
          const offset = headerHeight + filterBarHeight + announcementHeight;
          contentBelowFilter.style.paddingTop = offset + 'px';
        }
      }

      updateHeaderStyles(true);
    }

    function updateHeaderStyles(visible) {
      const styles = { background: visible ? 'black' : 'transparent' };

      gsap.to(header, {
        ...styles,
        duration: DURATION,
        ease: EASE
      });

      if (filterBar) {
        gsap.to(filterBar, {
          ...styles,
          duration: DURATION,
          ease: EASE
        });
      }

      if (isTouchDevice && isNotCollectionPage) {
        if (visible && getScrollY() > 50) {
          header.classList.add('fff');
        } else {
          header.classList.remove('fff');
        }
      }
    }

    function setAnnouncementVisible(visible, animate) {
      if (!announcementBar) return;
      if (visible === isAnnouncementVisible && !animate) return;

      isAnnouncementVisible = visible;

      if (announcementTween) announcementTween.kill();

      const targetY = visible ? 0 : -announcementHeight;

      if (animate) {
        announcementTween = gsap.to(announcementBar, {
          y: targetY,
          duration: DURATION,
          ease: EASE
        });
      } else {
        gsap.set(announcementBar, { y: targetY });
      }

      animateHeader(isHeaderVisible, true);
    }

    function animateHeader(shouldBeVisible, force) {
      if (!force && shouldBeVisible === isHeaderVisible) return;

      isHeaderVisible = shouldBeVisible;

      if (headerTween) headerTween.kill();
      if (filterTween) filterTween.kill();

      const headerTargetY = shouldBeVisible
        ? (isAnnouncementVisible && announcementBar ? announcementHeight : 0)
        : -headerHeight;

      headerTween = gsap.to(header, {
        y: headerTargetY,
        duration: DURATION,
        ease: EASE
      });

      if (filterBar) {
        const filterTargetY = shouldBeVisible
          ? (isAnnouncementVisible && announcementBar
              ? announcementHeight + headerHeight
              : headerHeight)
          : 0;

        filterTween = gsap.to(filterBar, {
          y: filterTargetY,
          duration: DURATION,
          ease: EASE
        });
      }

      updateHeaderStyles(shouldBeVisible);
    }

    function onScrollFrame() {
      isTicking = false;

      const currentScrollY = lastKnownScrollY;
      const atTop = currentScrollY <= TOP_EPSILON;
      const wasAtTop = lastScrollY <= TOP_EPSILON;

      const delta = currentScrollY - lastScrollY;
      const isScrollingDown = delta > SCROLL_BUFFER;
      const isScrollingUp = delta < -SCROLL_BUFFER;

      // Direction of THIS frame: 1 = down, -1 = up, 0 = within the jitter buffer.
      const frameDir = isScrollingDown ? 1 : isScrollingUp ? -1 : 0;

      // Build a streak of consecutive same-direction frames. A real scroll holds a
      // direction for many frames; a Safari momentum reversal is a single frame, so
      // it resets the streak to 1 and never reaches CONFIRM_FRAMES — it can't toggle.
      if (frameDir !== 0) {
        if (frameDir === pendingDir) {
          dirStreak += 1;
        } else {
          pendingDir = frameDir;
          dirStreak = 1;
        }
      }

      const confirmedDown = pendingDir === 1 && dirStreak >= CONFIRM_FRAMES;
      const confirmedUp = pendingDir === -1 && dirStreak >= CONFIRM_FRAMES;

      // Announcement bar
      if (atTop && !wasAtTop) {
        setAnnouncementVisible(true, true);
      } else if (!atTop && wasAtTop) {
        setAnnouncementVisible(false, false);
      }

      // Header visibility: hide on down, show on up (only once the direction is confirmed)
      if (atTop) {
        animateHeader(true);
      } else {
        if (confirmedDown && isHeaderVisible) {
          animateHeader(false);
        } else if (confirmedUp && !isHeaderVisible) {
          animateHeader(true);
        }
      }

      lastScrollY = currentScrollY;
    }

    function handleScroll() {
      lastKnownScrollY = getScrollY();
      if (!isTicking) {
        isTicking = true;
        requestAnimationFrame(onScrollFrame);
      }
    }

    function handleResize() {
      const widthChanged = window.innerWidth !== lastWindowWidth;
      lastWindowWidth = window.innerWidth;

      // Always re-baseline the scroll position: the iOS toolbar collapse shifts the
      // scroll offset, and without this the next frame would see a fake jump.
      lastScrollY = getScrollY();
      lastKnownScrollY = lastScrollY;

      // Height-only resize = iOS Safari showing/hiding its URL toolbar mid-scroll.
      // Do NOT force the header visible here — that force-show is exactly what makes
      // the logo reappear-then-hide ("jump") on Safari mobile during a fast scroll.
      // Leave the header in whatever state the scroll logic put it in. Desktop never
      // hits this path because its window doesn't resize while scrolling.
      if (!widthChanged) {
        return;
      }

      // Genuine resize (orientation change / window drag): recompute and reset clean.
      updateHeights();
      const atTop = lastScrollY <= TOP_EPSILON;
      if (announcementBar) {
        setAnnouncementVisible(atTop, false);
      }
      animateHeader(true, true);
    }

    function addEventListeners() {
      window.addEventListener('scroll', handleScroll, passiveOptions);
      window.addEventListener('resize', handleResize);
      // Removed touchmove listener to avoid noisy direction flips on iOS
    }

    function init() {
      updateHeights();
      lastScrollY = getScrollY();
      lastKnownScrollY = lastScrollY;

      const atTop = lastScrollY <= TOP_EPSILON;
      if (announcementBar) {
        isAnnouncementVisible = atTop;
        gsap.set(announcementBar, {
          y: isAnnouncementVisible ? 0 : -announcementHeight
        });
      }

      animateHeader(true, true);
      addEventListeners();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  initHeader();
})();
